/**
 * Utilidades comunes de las páginas de traspaso P2P (emisor y receptor).
 */

import type { AbstractPowerSyncDatabase } from "@powersync/common";
import {
  createSignalingClient,
  type SignalingClient,
  SignalingError,
  type SnapshotDb,
  type TransferErrorKind,
  TransferProtocolError,
} from "@stockpilot/shared";
import { SERVER_BASE_URL } from "@/lib/api";

/**
 * Cliente de señalización contra el backend de esta build. Los endpoints
 * `/api/p2p/*` son públicos (el receptor puede ser un dispositivo sin cuenta),
 * así que basta el fetch de la plataforma sin credenciales.
 */
export function createP2pSignalingClient(): SignalingClient {
  return createSignalingClient({ baseUrl: SERVER_BASE_URL.replace(/\/$/, "") });
}

/** Colapsa cualquier fallo del flujo en un `TransferErrorKind` para la UI. */
export function mapTransferError(error: unknown): TransferErrorKind {
  if (error instanceof SignalingError) {
    return error.kind;
  }
  if (error instanceof TransferProtocolError) {
    return error.reason === "too-large" ? "too-large" : "transfer-failed";
  }
  if (error instanceof Error) {
    if (error.message === "p2p-connect-timeout" || error.message === "p2p-connection-failed") {
      return "connection-failed";
    }
    if (error.message === "p2p-signaling-failed") {
      return "network";
    }
  }
  return "transfer-failed";
}

/**
 * Puente estructural PowerSync → `SnapshotDb` (la superficie mínima que usan
 * buildSnapshot/importSnapshot) sin aserciones de tipo.
 */
export function toSnapshotDb(db: AbstractPowerSyncDatabase): SnapshotDb {
  return {
    getAll: async (sql, params) => db.getAll(sql, params),
    execute: async (sql, params) => db.execute(sql, params),
    writeTransaction: async (fn) =>
      db.writeTransaction(async (tx) =>
        fn({
          getAll: async (sql, params) => tx.getAll(sql, params),
          execute: async (sql, params) => tx.execute(sql, params),
        }),
      ),
  };
}
